import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

/**
 * Add-a-position picker for bar deliveries.
 *
 * `options` are rows of { key, label, sublabel, available }. What `available`
 * means depends on the flow: warehouse stock for an outbound delivery, what the
 * bar still holds for a goods return, and the outstanding crates of a
 * Pfandgruppe for an empties return. `key` is the option's identity — an
 * article id for goods, group id plus return rate for empties, since one
 * Pfandgruppe can be added twice at two different rates.
 *
 * `strictCap` colours a zero-or-less balance as an error. Empties pass it as
 * false: a bar can legitimately hand back more crates than it received, so a
 * depleted pool there is a note, not a fault.
 */
const BarArticlePicker = ({
  options,
  addedKeys,
  onAdd,
  showStock = true,
  title = 'Artikel hinzufügen',
  searchPlaceholder = 'Artikel suchen...',
  emptyLabel = 'Keine Artikel für Bar-Verkauf freigegeben.',
  noMatchLabel = 'Keine weiteren Artikel gefunden.',
  balanceTitle = 'verfügbarer Bestand',
  strictCap = true,
}) => {
  const [search, setSearch] = useState('');

  const available = useMemo(() => {
    const term = search.trim().toLowerCase();
    return options
      .filter((o) => !addedKeys.includes(o.key))
      .filter((o) => !term || o.label.toLowerCase().includes(term));
  }, [options, addedKeys, search]);

  return (
    <div className='card'>
      <div className='card-body'>
        <h6 className='card-title'>{title}</h6>
        <input
          type='text'
          className='form-control mb-2'
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {available.length === 0 ? (
          <p className='text-muted mb-0'>
            {options.length === 0 ? emptyLabel : noMatchLabel}
          </p>
        ) : (
          <div
            className='list-group'
            style={{ maxHeight: '320px', overflowY: 'auto' }}
          >
            {available.map((option) => (
              <button
                type='button'
                key={option.key}
                className='list-group-item list-group-item-action d-flex justify-content-between align-items-center'
                onClick={() => onAdd(option)}
              >
                <span>
                  {option.label}
                  {option.sublabel && (
                    <span className='text-muted ms-2'>({option.sublabel})</span>
                  )}
                </span>
                <span>
                  {showStock && (
                    <span
                      className={`badge me-2 ${
                        option.available > 0 || !strictCap
                          ? 'bg-secondary'
                          : 'bg-danger'
                      }`}
                      title={balanceTitle}
                    >
                      {option.available}
                    </span>
                  )}
                  <FontAwesomeIcon icon={faPlus} />
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BarArticlePicker;
